// cacheBust.js
//
// Two sites answer for departureboard.io — this one and the new one — and which
// one you get depends on a cookie the edge reads. The browser's HTTP cache knows
// nothing about that: it is keyed by URL, so `departureboard.io/` can hold a
// copy of whichever site the browser saw last and serve it back without ever
// making a request. No request means no edge, no cookie, and no routing.
//
// So the two sites keep their pages in separate URL namespaces, distinguished by
// the `cb` parameter:
//
//   cb=1   this site, the old one
//   cb=2   the new one — every link it writes carries it
//
// A cached hit inside a namespace is then always the right site's page, and
// crossing between them means asking for a URL whose worst case is a cache entry
// belonging to the site you were going to anyway.
//
// This site does not stamp its own links — that would put a parameter on every
// URL every visitor sees, for the sake of the few crossing between the two. It
// receives `cb=1` rather than writing it: the new site sends somebody here on
// that URL when they leave, which is the case that needs a namespace of its own,
// because the URL they are leaving from is one it has cached.

const PARAM = "cb";
const NEW_SITE = "2";

/** Whether the address bar is already in the new site's namespace. */
export const isNewSiteURL = () => {
  try {
    return new URL(window.location.href).searchParams.get(PARAM) === NEW_SITE;
  } catch (error) {
    console.error("Error reading the cache namespace:", error);
    return false;
  }
};

const go = (path, stamp, carry) => {
  const url = new URL(path, window.location.origin);
  if (carry) {
    carry.forEach((value, name) => url.searchParams.set(name, value));
  }
  url.searchParams.set(PARAM, stamp);
  // replace rather than assign, so the handover doesn't sit in the history and
  // send them back to a cache hit when they hit back.
  window.location.replace(url.toString());
};

// The three values an installed home screen shortcut launches with, if this page
// load is one of those launches.
//
// An iOS home screen app has its own cookie jar, so a shortcut installed under
// the old scheme has no beta cookie however many times its owner has opted in
// elsewhere — every launch therefore lands *here*, on this site, whatever the
// account says. This is the only moment the new site can be given the credential
// that shortcut carries: it is in this URL and it is nowhere else, because an
// installed start_url cannot be changed and there is no second copy of it.
//
// Handing over to a bare `/` threw it away. The container arrived on the new
// site holding the claim-less custom token this site's login transfer had just
// minted, which that API refuses on every route, and with nothing left to
// exchange for a real one. The new site reads these three back off the URL
// (`readLegacyLaunchCredential`) and trades them at
// `POST /api/v1/homescreen/session/legacy` for a proper session.
const LAUNCH_PARAMS = ["userID", "key", "token"];

const launchCredential = () => {
  const params = new URL(window.location.href).searchParams;
  if (!LAUNCH_PARAMS.every((name) => params.get(name))) {
    return null;
  }

  const carry = new URLSearchParams();
  LAUNCH_PARAMS.forEach((name) => carry.set(name, params.get(name)));
  return carry;
};

// Hands the browser over to the new site.
//
// A full document navigation, because a router push makes no request at all and
// so changes nothing; and to `/` rather than the page they are on, because the
// page they are on may not exist over there.
//
// Called only when the cookie is set. Either the request reaches the edge, which
// reads the cookie and answers with the new site, or the browser has `/?cb=2`
// cached already — which is a copy of the new site, since only the new site
// serves that namespace. Both outcomes are the intended destination.
export const handOverToNewSite = () => {
  try {
    // Already asked and still here: the edge is not routing this browser, and
    // asking again in a loop would only take the site down for them.
    if (isNewSiteURL()) {
      return;
    }

    // A launching shortcut keeps its path as well as its credential. The
    // journey URLs are the same shape on both sites, and dropping somebody on
    // the front page every time they open their commute would be a worse
    // shortcut than the one they had. Everything else still goes to `/`: an
    // ordinary page here may not exist over there.
    const carry = launchCredential();
    if (carry) {
      go(window.location.pathname, NEW_SITE, carry);
      return;
    }

    go("/", NEW_SITE);
  } catch (error) {
    console.error("Error handing over to the new site:", error);
  }
};

// This site's own namespace, for the one kind of link it does have to stamp.
const OLD_SITE = "1";

// The parameters that describe *how* a page was opened rather than the page
// itself: the cache namespace, and a launching shortcut's credential under
// either scheme. The old site's three are the ones installed shortcuts carry;
// the new site's pair arrives here too, whenever a shortcut added over there
// launches into a container that has no beta cookie in it and is therefore
// routed to this site.
const TRANSIENT_PARAMS = [PARAM, ...LAUNCH_PARAMS, "k", "s"];

// A URL reduced to the page it names, for anything that stores one or offers it
// back later.
//
// The "you've navigated away from the page you saved to your homescreen" strip
// is the caller. It compares what it stored against the page you are on, and it
// puts what it stored behind a link — so a stored URL carrying a namespace or a
// one-shot credential is both a page that never quite matches itself and a
// credential handed back to the browser long after the launch it was for.
//
// The rest of the query is left alone: on a journey it *is* the page.
export const pageOnly = (path) => {
  try {
    const url = new URL(path, window.location.origin);
    TRANSIENT_PARAMS.forEach((name) => url.searchParams.delete(name));
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    console.error("Error reading the saved page:", error);
    return path;
  }
};

// The same path, in this site's cache namespace.
//
// This site does not stamp its links in general — see the note at the top of
// this file — but the "go back" strip is the one place it has to. That strip is
// only ever offered inside an installed home screen app, and a container that
// has been through the beta and come back out has a copy of the *new* site's
// page sitting in its cache under the bare URL. Sending somebody there
// unnamespaced can be answered straight out of that cache: no request, no edge,
// no routing, and the way back to the page they saved lands them on the site
// they just left.
export const withOldSiteParam = (path) => {
  try {
    const url = new URL(path, window.location.origin);
    url.searchParams.set(PARAM, OLD_SITE);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    console.error("Error stamping the cache namespace:", error);
    return path;
  }
};
